export interface ProcessedImages {
  fullImage: File;
  thumbnail: File;
}

export interface ImagePreviewData {
  originalImageUrl: string;
  cropArea: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  originalDimensions: {
    width: number;
    height: number;
  };
}

export type CropMode = '4:5' | '5:4' | 'freeform';

export interface CropData {
  x: number;
  y: number;
  width: number;
  height: number;
  scale: number;
  mode?: CropMode;
}

// Thumbnail dimensions and optimization settings
const THUMBNAIL_WIDTH = 512;
const THUMBNAIL_HEIGHT = 640;
const MAX_IMAGE_DIMENSION = 2048; // Max width or height before optimization
const OPTIMIZATION_QUALITY = 0.85; // JPEG quality for optimization

// Aspect ratio configurations
const ASPECT_RATIOS = {
  '4:5': 4 / 5,
  '5:4': 5 / 4,
  'freeform': null
} as const;

/**
 * Optimizes an image by resizing if too large and adjusting quality
 */
export function optimizeImage(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        // Calculate optimized dimensions
        let { width, height } = img;
        const maxDimension = Math.max(width, height);
        
        if (maxDimension > MAX_IMAGE_DIMENSION) {
          const scale = MAX_IMAGE_DIMENSION / maxDimension;
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to optimize image'));
              return;
            }
            
            const optimizedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            
            resolve(optimizedFile);
          },
          'image/jpeg',
          OPTIMIZATION_QUALITY
        );
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Generates preview data showing how the image will be cropped to 4:5
 */
export function generateImagePreview(file: File): Promise<ImagePreviewData> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      try {
        // Calculate 4:5 crop dimensions
        const targetAspectRatio = 4 / 5;
        const currentAspectRatio = img.width / img.height;

        let cropWidth = img.width;
        let cropHeight = img.height;
        let cropX = 0;
        let cropY = 0;

        if (currentAspectRatio > targetAspectRatio) {
          // Image is wider than 4:5, crop width
          cropWidth = img.height * targetAspectRatio;
          cropX = (img.width - cropWidth) / 2;
        } else if (currentAspectRatio < targetAspectRatio) {
          // Image is taller than 4:5, crop height
          cropHeight = img.width / targetAspectRatio;
          cropY = (img.height - cropHeight) / 2;
        }

        resolve({
          originalImageUrl: img.src,
          cropArea: {
            x: cropX,
            y: cropY,
            width: cropWidth,
            height: cropHeight,
          },
          originalDimensions: {
            width: img.width,
            height: img.height,
          },
        });
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load image for preview'));
    };

    // Load the image
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        img.src = e.target.result as string;
      }
    };
    reader.onerror = () => {
      reject(new Error('Failed to read file for preview'));
    };
    reader.readAsDataURL(file);
  });
};

/**
 * Crops an image to 4:5 aspect ratio and generates a thumbnail
 */
export function processImageForUpload(file: File): Promise<ProcessedImages> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      reject(new Error('Could not get canvas context'));
      return;
    }

    const img = new Image();
    
    img.onload = () => {
      try {
        // Calculate 4:5 crop dimensions
        const targetAspectRatio = 4 / 5;
        const currentAspectRatio = img.width / img.height;

        let cropWidth = img.width;
        let cropHeight = img.height;
        let cropX = 0;
        let cropY = 0;

        if (currentAspectRatio > targetAspectRatio) {
          // Image is wider than 4:5, crop width
          cropWidth = img.height * targetAspectRatio;
          cropX = (img.width - cropWidth) / 2;
        } else if (currentAspectRatio < targetAspectRatio) {
          // Image is taller than 4:5, crop height
          cropHeight = img.width / targetAspectRatio;
          cropY = (img.height - cropHeight) / 2;
        }

        // Create full-size cropped image (max 2048x2560 for 2K quality)
        const maxWidth = 2048;
        const maxHeight = 2560;
        let fullWidth = cropWidth;
        let fullHeight = cropHeight;

        if (cropWidth > maxWidth || cropHeight > maxHeight) {
          const scale = Math.min(maxWidth / cropWidth, maxHeight / cropHeight);
          fullWidth = cropWidth * scale;
          fullHeight = cropHeight * scale;
        }

        // Draw full-size image
        canvas.width = fullWidth;
        canvas.height = fullHeight;
        ctx.drawImage(img, cropX, cropY, cropWidth, cropHeight, 0, 0, fullWidth, fullHeight);

        canvas.toBlob((fullBlob) => {
          if (!fullBlob) {
            reject(new Error('Failed to create full image blob'));
            return;
          }

          // Create thumbnail (320x400 for 4:5)
          const thumbnailWidth = THUMBNAIL_WIDTH;
          const thumbnailHeight = THUMBNAIL_HEIGHT;
          
          canvas.width = thumbnailWidth;
          canvas.height = thumbnailHeight;
          ctx.drawImage(img, cropX, cropY, cropWidth, cropHeight, 0, 0, thumbnailWidth, thumbnailHeight);

          canvas.toBlob((thumbnailBlob) => {
            if (!thumbnailBlob) {
              reject(new Error('Failed to create thumbnail blob'));
              return;
            }

            // Create File objects
            const originalName = file.name.replace(/\.[^/.]+$/, ''); // Remove extension
            const extension = file.name.split('.').pop() || 'jpg';

            const fullImage = new File([fullBlob], `${originalName}_cropped.${extension}`, {
              type: file.type,
            });

            const thumbnail = new File([thumbnailBlob], `${originalName}_thumb.${extension}`, {
              type: file.type,
            });

            resolve({
              fullImage,
              thumbnail,
            });
          }, file.type, 0.9); // High quality for thumbnail

        }, file.type, 0.95); // Very high quality for full image

      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    // Load the image
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        img.src = e.target.result as string;
      }
    };
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    reader.readAsDataURL(file);
  });
};

/**
 * Processes an image with user-defined crop area, scale, and crop mode
 */
export const processImageWithCropArea = async (
  file: File, 
  cropArea: { x: number; y: number; width: number; height: number }, 
  cropMode: CropMode = '4:5'
): Promise<ProcessedImages> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      reject(new Error('Could not get canvas context'));
      return;
    }

    img.onload = () => {
      try {
        // Calculate actual crop dimensions based on the crop area
        const cropWidth = cropArea.width;
        const cropHeight = cropArea.height;
        
        // Set canvas size to crop size
        canvas.width = cropWidth;
        canvas.height = cropHeight;
        
        // Draw the cropped image
        ctx.drawImage(
          img,
          cropArea.x, cropArea.y, cropWidth, cropHeight,
          0, 0, cropWidth, cropHeight
        );
        
        // Convert to blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to process image'));
              return;
            }
            
            const fullImage = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            
            // Create thumbnail
            const thumbnailCanvas = document.createElement('canvas');
            const thumbnailCtx = thumbnailCanvas.getContext('2d');
            
            if (!thumbnailCtx) {
              reject(new Error('Could not get thumbnail canvas context'));
              return;
            }
            
            // Calculate thumbnail dimensions based on crop mode
            let thumbWidth = THUMBNAIL_WIDTH;
            let thumbHeight = THUMBNAIL_HEIGHT;
            
            if (cropMode === '5:4') {
              thumbWidth = THUMBNAIL_HEIGHT; // 640
              thumbHeight = Math.round(thumbWidth / (5/4)); // 512
            } else if (cropMode === 'freeform') {
              // For freeform, maintain aspect ratio but fit within thumbnail bounds
              const aspectRatio = cropWidth / cropHeight;
              if (aspectRatio > 1) {
                thumbWidth = THUMBNAIL_HEIGHT;
                thumbHeight = Math.round(thumbWidth / aspectRatio);
              } else {
                thumbHeight = THUMBNAIL_HEIGHT;
                thumbWidth = Math.round(thumbHeight * aspectRatio);
              }
            }
            
            thumbnailCanvas.width = thumbWidth;
            thumbnailCanvas.height = thumbHeight;
            
            // Draw thumbnail
            thumbnailCtx.drawImage(canvas, 0, 0, thumbWidth, thumbHeight);
            
            // Convert thumbnail to blob
            thumbnailCanvas.toBlob(
              (thumbnailBlob) => {
                if (!thumbnailBlob) {
                  reject(new Error('Failed to create thumbnail'));
                  return;
                }
                
                const thumbnail = new File([thumbnailBlob], `thumb_${file.name}`, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                
                resolve({ fullImage, thumbnail });
              },
              'image/jpeg',
              0.8
            );
          },
          'image/jpeg',
          0.9
        );
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Processes an image with user-defined crop area and scale from interactive cropper
 */
export const processImageWithCropData = async (file: File, cropData: CropData): Promise<ProcessedImages> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Canvas context not available'));
      return;
    }

    img.onload = () => {
      try {
        // Since FullPageImageCropper already provides coordinates in natural image space,
        // we can use them directly
        const finalCropX = Math.max(0, Math.min(cropData.x, img.naturalWidth - cropData.width));
        const finalCropY = Math.max(0, Math.min(cropData.y, img.naturalHeight - cropData.height));
        const finalCropWidth = Math.min(cropData.width, img.naturalWidth - finalCropX);
        const finalCropHeight = Math.min(cropData.height, img.naturalHeight - finalCropY);

        // Create full-size cropped image (max 2048x2560 for 2K quality)
        const maxWidth = 2048;
        const maxHeight = 2560;
        let fullWidth = finalCropWidth;
        let fullHeight = finalCropHeight;

        if (finalCropWidth > maxWidth || finalCropHeight > maxHeight) {
          const scale = Math.min(maxWidth / finalCropWidth, maxHeight / finalCropHeight);
          fullWidth = finalCropWidth * scale;
          fullHeight = finalCropHeight * scale;
        }

        // Draw full-size image
        canvas.width = fullWidth;
        canvas.height = fullHeight;
        ctx.drawImage(
          img, 
          finalCropX, finalCropY, finalCropWidth, finalCropHeight,
          0, 0, fullWidth, fullHeight
        );

        canvas.toBlob((fullBlob) => {
          if (!fullBlob) {
            reject(new Error('Failed to create full image blob'));
            return;
          }

          // Create thumbnail (320x400 for 4:5)
          const thumbnailWidth = THUMBNAIL_WIDTH;
          const thumbnailHeight = THUMBNAIL_HEIGHT;
          
          canvas.width = thumbnailWidth;
          canvas.height = thumbnailHeight;
          ctx.drawImage(
            img, 
            finalCropX, finalCropY, finalCropWidth, finalCropHeight,
            0, 0, thumbnailWidth, thumbnailHeight
          );

          canvas.toBlob((thumbnailBlob) => {
            if (!thumbnailBlob) {
              reject(new Error('Failed to create thumbnail blob'));
              return;
            }

            // Create File objects
            const originalName = file.name.replace(/\.[^/.]+$/, ''); // Remove extension
            const extension = file.name.split('.').pop() || 'jpg';

            const fullImage = new File([fullBlob], `${originalName}_cropped.${extension}`, {
              type: file.type,
            });

            const thumbnail = new File([thumbnailBlob], `${originalName}_thumb.${extension}`, {
              type: file.type,
            });

            resolve({
              fullImage,
              thumbnail,
            });
          }, file.type, 0.9); // High quality for thumbnail

        }, file.type, 0.95); // Very high quality for full image

      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    // Load the image
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        img.src = e.target.result as string;
      }
    };
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    reader.readAsDataURL(file);
  });
};
